<template>
  <aside class="side-rail">
    <!-- CURRENT / TODAY, with quick play as the card's footer. -->
    <section class="side-rail__card side-rail__card--list">
      <div class="side-rail__tabs">
        <button
          type="button"
          class="side-rail__tab"
          :class="{ active: tab === 'current' }"
          @click="tab = 'current'"
        >
          {{ $t('Current') }}
        </button>
        <button
          type="button"
          class="side-rail__tab"
          :class="{ active: tab === 'today' }"
          @click="tab = 'today'"
        >
          <!-- Label only: `today` stays the tab's key, so nothing downstream moves. -->
          {{ $t('History') }}
        </button>
      </div>

      <!--
        A bet's detail opens here rather than in the shared popup, which on
        desktop covers half the screen. The tabs stay put above it, so the back
        bar is all that is needed to get out. `bet-history-detail.vue` reads the
        ticket from the store, so it renders correctly anywhere.
      -->
      <div v-if="isDetailOpen" class="side-rail__body">
        <button type="button" class="side-rail__back" @click="closeDetail">
          <span class="side-rail__back-chevron">‹</span>
          {{ $t('Bet detail') }}
        </button>
        <BetHistoryDetail class="side-rail__detail" />
      </div>

      <!--
        Hidden rather than destroyed: `bet-history.vue` keeps its date range,
        product filter and page in its own data, so a v-if here threw all of it
        away every time a bet detail was opened or the tab was switched. It is
        still mounted lazily, so CURRENT does not pay for its fetch on load.
      -->
      <div v-show="!isDetailOpen" class="side-rail__body">
        <transition-group
          v-show="tab === 'current'"
          tag="ul"
          name="rail-row"
          class="side-rail__rows"
        >
          <RailBetRow v-for="row in currentRows" :key="row.id" :row="row" />
        </transition-group>

        <!--
          TODAY is the mobile bet history verbatim: menus/bet-history.vue already
          owns the date select, the All / Tài Xỉu / cockfight filters, the
          turnover-ante-W/L summary and the paginated table, so it is mounted
          as-is rather than rebuilt for desktop.
        -->
        <BetHistory
          v-if="historyMounted"
          v-show="tab === 'today'"
          class="side-rail__history"
        />
      </div>

      <!--
        Tài Xỉu quick play: the card's footer bar (v2.1), whose board opens as a
        sheet over the list above it. With no Tài Xỉu table open there is
        nothing to play, so it is not rendered.
      -->
      <section v-if="hasTaiXiuTables" class="side-rail__quickplay">
        <QuickPlayPanel />
      </section>
    </section>
  </aside>
</template>

<script>
/*
 * The desktop left rail: CURRENT / TODAY over the Tài Xỉu quick-play card.
 *
 * CURRENT is the member's newest bets, from two sources:
 *  - the tickets endpoint gives the bet itself (table, round, selection, stake,
 *    odds, and the settled result once it exists);
 *  - the sockets give the live status of the round that bet is on, which the
 *    ticket payload cannot know -- it was written when the bet was placed.
 *
 * So a ticket whose fight is still the live one is labelled from the socket
 * (BET OPEN / CLOSING SOON / BET CLOSED); anything older is labelled from its
 * own settled result.
 */
import { mapActions, mapGetters } from 'vuex';
import { formatAmount } from '@/utils/helpers';
import { PopupPage } from '@/utils/index.model';
import { cockfightPaneNamespace } from '@/store/cockfight-pane';
import { placedOddsKey } from '@/store/modules/ticket';
import { formatTaiXiuOdds, taiXiuGameOdds, taiXiuIconMarket, taiXiuSelectionMarket } from '@/utils/tai-xiu-market';
import { TaiXiuBetType, normalizeTaiXiuTicketBetType } from '@/utils/index.model';
import {
  getSubTickets,
  isMultiSelectionTicket,
  isTicketPending,
  subTicketsFromSummary,
} from '@/utils/ticket-selections';
import BetHistory from '@/components/menus/bet-history.vue';
import QuickPlayPanel from '@/components/desktop/quick-play-panel.vue';
import RailBetRow from '@/components/desktop/rail-bet-row.vue';
import BetHistoryDetail from '@/components/menus/bet-history-detail.vue';

// The design shows five. A sixth arriving pushes the oldest out, which the
// transition-group fades.
const MAX_CURRENT_ROWS = 5;
// Socket ticks arrive in bursts; one refetch per burst is enough.
const RELOAD_DEBOUNCE_MS = 1500;
// Refetches while a round has its result but the bet is not settled yet.
const SETTLE_RETRY_MS = 3000;
const SETTLE_RETRY_LIMIT = 5;
// A settled bet stays on the list this long, then leaves.
const SETTLED_LINGER_MS = 15000;
/*
 * When each ticket was first seen settled, kept in sessionStorage. The payload
 * has no settlement time, so the countdown starts from the first sighting --
 * and without persisting it a reload started every countdown again, which is
 * why a bet that had already left the list came back.
 */
const SETTLED_AT_KEY = 'ga28.rail.settledAt';
/*
 * When each ticket was first seen on a live round, kept the same way. Only these
 * tickets may stay on after their round is replaced: the endpoint returns the
 * whole last day, and an older bet must not come back.
 */
const TRACKED_AT_KEY = 'ga28.rail.trackedAt';
// Long enough to outlive any round, short enough that the entry never grows.
const SETTLED_AT_TTL_MS = 60 * 60 * 1000;

const readStoredTimes = (key) => {
  try {
    const stored = JSON.parse(window.sessionStorage.getItem(key) || '{}');
    const cutoff = Date.now() - SETTLED_AT_TTL_MS;
    return Object.keys(stored).reduce((kept, id) => {
      if (Number(stored[id]) > cutoff) kept[id] = Number(stored[id]);
      return kept;
    }, {});
  } catch (error) {
    // Private mode, cleared storage: the list just starts its countdowns again.
    return {};
  }
};

const writeStoredTimes = (key, value) => {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    // Nothing to do: the countdown still works for this page view.
  }
};
// `status` on the tickets endpoint: 4 is settled.
const TICKET_STATUS_SETTLED = 4;
// A bet that was cancelled or voided is finished too, and says so in `result`.
const FINISHED_RESULTS = ['cancelled', 'void'];

/*
 * A live round's status as the row shows it, in the backend's order
 * (FightStatus, utils/index.model.ts). Anything past `ended` -- declared,
 * approved, processing, processed -- means the result is in.
 */
const LIVE_STATUSES = {
  confirmed: { label: 'Bet Open', key: 'open' },
  closing: { label: 'Closing Soon', key: 'closing' },
  closed: { label: 'Bet Closed', key: 'closed' },
  started: { label: 'Started', key: 'started' },
  ended: { label: 'Ended', key: 'ended' },
  cancelling: { label: 'Cancelled', key: 'void' },
  cancelled: { label: 'Cancelled', key: 'void' },
};
const RESULT_STATUS = { label: 'Result', key: 'result' };
// Cockfight odds keep moving while betting is open, so a row shows none until
// the round has closed.
const COCKFIGHT_BET_OPEN_STATUSES = ['created', 'confirmed', 'closing'];
const NO_ODDS = '–';
// Statuses before the result: a round still in one of these has no result yet.
const PRE_RESULT_STATUSES = ['created', 'confirmed', 'closing', 'closed', 'started', 'ended'];

const TAI_XIU_SELECTIONS = ['tai', 'tài', 'big', 'xiu', 'xỉu', 'small', 'le', 'lẻ', 'odd', 'chan', 'chẵn', 'even'];

const SIDE_LABELS = {
  banker: 'B',
  player: 'P',
  draw: 'D',
};

/*
 * `bet_type` on the ticket (either numbering, normalised to TaiXiuBetType by
 * `normalizeTaiXiuTicketBetType`), which is what actually says
 * which Tài Xỉu market a bet is on -- the selection alone is often just a
 * number ("5" is a die face under `single` and a total under `total5`), so
 * matching on words left every numeric market with no pill at all.
 */
// The face markets, badged with their dice (TxMarketBadge).
const TX_FACE_BET_TYPES = [TaiXiuBetType.single, TaiXiuBetType.double, TaiXiuBetType.triple];

export default {
  name: 'SideRail',
  components: {
    BetHistory,
    QuickPlayPanel,
    RailBetRow,
    BetHistoryDetail,
  },
  data() {
    return {
      tab: 'current',
      // ticketId -> when this client first saw it settled. The payload carries
      // no settlement time, so the countdown starts from the first sighting.
      settledAt: readStoredTimes(SETTLED_AT_KEY),
      // ticketId -> when it was first seen on a live round (TRACKED_AT_KEY).
      trackedAt: readStoredTimes(TRACKED_AT_KEY),
      // Ticks while a settled row is waiting to leave, so `currentRows` re-runs.
      now: Date.now(),
      expiryTimer: null,
      // Latched on the first visit to TODAY and never cleared, so the history
      // keeps its filters for the rest of the session.
      historyMounted: false,
      formatAmount,
      reloadTimer: null,
      settleRetries: 0,
      // `${fightId}:${selection}` -> a cockfight round's final odds, kept once
      // it has closed so they outlive the round being replaced on screen.
      finalOdds: {},
    };
  },
  computed: {
    ...mapGetters('ticket', ['recentTickets', 'hasPlacedBet', 'placedOdds']),
    ...mapGetters('fight', ['fight']),
    ...mapGetters('taiXiu', ['tables']),
    ...mapGetters('general', ['selectedArena', 'arenas', 'timezone']),
    ...mapGetters('menu', ['activePopupPage', 'isPopupOpen']),
    hasTaiXiuTables() {
      return Boolean(this.tables && this.tables.length);
    },

    /*
     * Live rounds keyed by fight id, so a ticket can be matched to one in O(1).
     * Cockfight contributes the root fight and each desktop pane's own fight
     * (a pane can follow a different arena); Tài Xỉu contributes one per
     * table, since every table runs its own round.
     */
    // The live cockfight rounds (root + each pane), keyed by fight id.
    cockfightFightsById() {
      const map = {};
      if (this.fight && this.fight.id) map[this.fight.id] = this.fight;
      ['a', 'b'].forEach((paneId) => {
        const paneFight = this.$store.getters[`${cockfightPaneNamespace(paneId)}/fight/fight`];
        if (paneFight && paneFight.id) map[paneFight.id] = paneFight;
      });
      return map;
    },
    liveStatusByFightId() {
      const map = {};
      if (this.fight && this.fight.id) map[this.fight.id] = this.fight.status;
      ['a', 'b'].forEach((paneId) => {
        const paneFight = this.$store.getters[`${cockfightPaneNamespace(paneId)}/fight/fight`];
        if (paneFight && paneFight.id) map[paneFight.id] = paneFight.status;
      });
      (this.tables || []).forEach((table) => {
        if (table.currentFightId) map[table.currentFightId] = table.fightStatus;
      });
      return map;
    },

    /*
     * A digest of every table's round state. Watching the array itself would
     * not fire -- the store replaces table objects wholesale on each socket
     * tick, but the reference churn is constant, so this collapses it to the
     * one thing that matters: did any round change state.
     */
    // The same for cockfight: the root fight and each desktop pane's own.
    cockfightStatuses() {
      return Object.keys(this.cockfightFightsById)
        .map((fightId) => `${fightId}:${this.cockfightFightsById[fightId].status || ''}`)
        .join('|');
    },

    taiXiuStatuses() {
      return (this.tables || []).map((t) => `${t.currentFightId || ''}:${t.fightStatus || ''}`).join('|');
    },

    /*
     * `tables/bet-history.vue` announces a detail by setting `activePopupPage`
     * -- on mobile it is inside the popup and just swaps its page. The rail
     * reads the same signal but renders in place instead of raising the popup,
     * so that component needs no change.
     */
    isDetailOpen() {
      return this.activePopupPage === PopupPage.betDetail;
    },

    /*
     * CURRENT: this round's bets, plus any bet seen live in this session whose
     * round has since moved on -- those stay until 15s after their result
     * (`hasExpired`), not until the next round starts.
     */
    currentRows() {
      return [...(this.recentTickets || [])]
        .filter((ticket) => ticket && ticket.id)
        .filter((ticket) => this.isCurrentRound(ticket) || this.trackedAt[ticket.id])
        .filter((ticket) => !this.hasExpired(ticket))
        // One row per bet: a multi-selection ticket is not combined here.
        .reduce((rows, ticket) => rows.concat(this.splitTicket(ticket)), [])
        .sort((a, b) => String(b.createdDateTime).localeCompare(String(a.createdDateTime)))
        .slice(0, MAX_CURRENT_ROWS)
        .map((ticket) => this.toRow(ticket));
    },
  },
  watch: {
    tab(value) {
      if (value === 'today') this.historyMounted = true;
    },

    /*
     * Refetch whenever a round changes state: that is when a bet of the
     * member's can settle, and the ticket payload only carries the result once
     * the backend has written it.
     */
    // Every cockfight round on screen, not just the root fight: a pane's round
    // (another arena) reaching its result never refetched, so its bet stayed
    // without a payout.
    cockfightStatuses: 'loadRecent',
    taiXiuStatuses: 'loadRecent',

    /*
     * Track every bet seen on a live round, and start its 15s the moment its
     * round reports a result -- the ticket itself may settle a little later.
     */
    liveStatusByFightId: 'trackLiveTickets',
    cockfightFightsById: { handler: 'captureFinalOdds', deep: true, immediate: true },
    recentTickets: 'trackLiveTickets',
  },
  mounted() {
    // Bets still running from before a reload come back here; new ones arrive
    // via `ticket/refreshAfterBet` (Tài Xỉu and cockfight submits).
    this.restoreRecentTickets();
    // One second is fine: the only thing it drives is a 15s expiry.
    this.expiryTimer = setInterval(() => {
      this.now = Date.now();
    }, 1000);
  },
  beforeDestroy() {
    clearTimeout(this.reloadTimer);
    clearInterval(this.expiryTimer);
  },
  methods: {
    ...mapActions('ticket', ['refreshRecentTickets', 'restoreRecentTickets']),
    ...mapActions('menu', ['updateMenu']),
    ...mapActions('ticket', ['resetTicket']),

    closeDetail() {
      this.resetTicket();
      // Back to whichever list the row was opened from.
      this.updateMenu({ activePopupPage: '' });
    },

    /*
     * Debounced: every table ticks its own round over the socket, so the
     * watchers fire in bursts. Without this a handful of tables turns into a
     * request per tick.
     */
    loadRecent() {
      // Nothing to follow until the member has bet in this page view.
      if (!this.hasPlacedBet) return;
      this.settleRetries = 0;
      clearTimeout(this.reloadTimer);
      this.reloadTimer = setTimeout(this.fetchRecent, RELOAD_DEBOUNCE_MS);
    },

    /*
     * The backend settles a ticket a moment after its round's last status, so
     * the fetch on that status can still find it unsettled -- and nothing else
     * would fetch again, leaving the row without its payout. While a bet's
     * round has its result but the ticket is not settled, fetch again shortly.
     */
    async fetchRecent() {
      try {
        await this.refreshRecentTickets();
      } catch (error) {
        // The next round change fetches again.
      }
      const awaitingSettle = (this.recentTickets || []).some(
        (ticket) => ticket && this.settledAt[ticket.id] && !this.isSettled(ticket)
      );
      if (!awaitingSettle || this.settleRetries >= SETTLE_RETRY_LIMIT) return;
      this.settleRetries += 1;
      clearTimeout(this.reloadTimer);
      this.reloadTimer = setTimeout(this.fetchRecent, SETTLE_RETRY_MS);
    },

    /*
     * Which product a bet belongs to.
     *
     * Decided by the venue, not by the selection text: Tài Xỉu also takes
     * numeric bets ("5" for a sum), which no keyword list can recognise and
     * which were landing in the cockfight column. The arena id on the ticket
     * joins straight to the Tài Xỉu table list; the keyword list is only a
     * fallback for when that list has not loaded yet.
     */
    isTaiXiu(ticket) {
      const arenaId = ticket.fight && ticket.fight.matchDay && ticket.fight.matchDay.arena
        ? ticket.fight.matchDay.arena.id
        : '';
      if (arenaId && (this.tables || []).some((t) => t.id === arenaId)) return true;
      return TAI_XIU_SELECTIONS.includes(String(ticket.selection || '').toLowerCase());
    },

    /*
     * The endpoint says so directly. This used to be inferred from "no live
     * status and some result", which got two cases wrong: a bet settled while
     * its fight was still the live one read as pending, and a cancelled bet
     * read as settled.
     */
    isSettled(ticket) {
      if (Number(ticket.status) === TICKET_STATUS_SETTLED) return true;
      return FINISHED_RESULTS.includes(String(ticket.result || '').toLowerCase());
    },

    /*
     * A bet leaves the list 15 seconds after its result. Neither payload carries
     * a result time, so the clock starts when this client first sees one --
     * the round's status passing `ended`, or the ticket coming back settled,
     * whichever is first. `now` ticks once a second to re-evaluate.
     */
    hasExpired(ticket) {
      if (this.isSettled(ticket)) this.markResult(ticket.id);
      const since = this.settledAt[ticket.id];
      return !!since && this.now - since >= SETTLED_LINGER_MS;
    },

    markResult(ticketId) {
      if (this.settledAt[ticketId]) return;
      this.$set(this.settledAt, ticketId, Date.now());
      writeStoredTimes(SETTLED_AT_KEY, this.settledAt);
    },

    trackLiveTickets() {
      let tracked = false;
      (this.recentTickets || []).forEach((ticket) => {
        if (!ticket || !ticket.id || !this.isCurrentRound(ticket)) return;
        if (!this.trackedAt[ticket.id]) {
          this.$set(this.trackedAt, ticket.id, Date.now());
          tracked = true;
        }
        if (this.hasResultStatus(this.liveStatusByFightId[ticket.fight.id])) this.markResult(ticket.id);
      });
      if (tracked) writeStoredTimes(TRACKED_AT_KEY, this.trackedAt);
    },

    hasResultStatus(liveStatus) {
      return !!liveStatus && !PRE_RESULT_STATUSES.includes(liveStatus) && !LIVE_STATUSES[liveStatus];
    },

    /*
     * CURRENT is this round's bets only. The tickets endpoint returns the last
     * day, so a bet from an earlier round that had already left the list came
     * back on the next fetch (a reload, a new bet); matching the ticket's
     * fight to a round that is live now keeps those out.
     */
    isCurrentRound(ticket) {
      const fightId = ticket.fight && ticket.fight.id;
      return !!fightId && Object.prototype.hasOwnProperty.call(this.liveStatusByFightId, fightId);
    },

    /*
     * A multi-selection ticket as one ticket per selection, each with its own
     * selection, bet type, odds, stake and result; the round (fight) is the
     * parent's. A ticket without `sub_tickets` but with a "4:31|17:31"
     * summary is split from that, with no stake of its own ("—").
     */
    splitTicket(ticket) {
      let subs = getSubTickets(ticket);
      if (!subs.length) subs = subTicketsFromSummary(ticket);
      if (subs.length <= 1) return [ticket];
      return subs.map((sub) => ({
        ...ticket,
        id: sub.id,
        selection: sub.selection,
        betType: sub.betType,
        odds: sub.odds,
        amount: sub.amount,
        result: sub.result || ticket.result,
        status: sub.status !== undefined && sub.status !== null ? sub.status : ticket.status,
        winLossAmount: sub.winLossAmount,
        createdDateTime: sub.createdDateTime || ticket.createdDateTime,
        subTickets: undefined,
        derived: !!sub.derived,
      }));
    },

    /*
     * The icon badge: dice for a single / double / triple, the dotted total
     * for a total. A total whose bet type does not match its selection (or is
     * missing) still reads as a total from the selection itself (4..17).
     */
    iconMarket(ticket) {
      return taiXiuSelectionMarket(ticket.betType, ticket.selection);
    },

    toRow(ticket) {
      const taiXiu = this.isTaiXiu(ticket);
      const fight = ticket.fight || {};
      const liveStatus = this.liveStatusByFightId[fight.id];
      const settled = this.isSettled(ticket);

      return {
        id: ticket.id,
        table: this.tableCode(ticket, taiXiu),
        round: fight.fightNumber || '',
        // Badge 1 is the Tài Xỉu view, badge 2 the cockfight one.
        screen: taiXiu ? 1 : 2,
        side: this.sideLabel(ticket, taiXiu),
        sideKey: this.sideKey(ticket, taiXiu),
        // Triple, total, single and double are icon badges (TxMarketBadge).
        market: taiXiu ? this.iconMarket(ticket) : null,
        stake: ticket.derived ? '—' : formatAmount(ticket.amount),
        ...(settled ? this.settledFields(ticket, taiXiu) : this.pendingFields(ticket, liveStatus, taiXiu)),
        ...(isMultiSelectionTicket(ticket) ? this.multiFields(ticket, taiXiu, settled) : {}),
        // Split from a summary: no W/L of its own to show once settled.
        ...(ticket.derived && settled ? { payout: '—', payoutKind: 'odds' } : {}),
      };
    },

    /*
     * Multi-selection ticket: "N BETS" in place of the market pill, no single
     * odds while it runs, and its selections for the row to open below itself.
     */
    multiFields(ticket, taiXiu, settled) {
      const subTickets = getSubTickets(ticket);
      return {
        betsCount: subTickets.length,
        market: null,
        ...(settled ? {} : { payout: '—', payoutKind: 'odds' }),
        selections: subTickets.map((sub) => {
          const pending = isTicketPending(sub);
          const amount = Number(sub.winLossAmount) || 0;
          return {
            id: sub.id,
            side: this.sideLabel(sub, taiXiu),
            sideKey: this.sideKey(sub, taiXiu),
            market: taiXiu ? taiXiuIconMarket(sub.betType, sub.selection) : null,
            stake: formatAmount(sub.amount),
            payout: pending ? this.runningOddsText(sub, taiXiu) : formatAmount(amount),
            payoutKind: pending ? 'odds' : amount >= 0 ? 'win' : 'loss',
          };
        }),
      };
    },

    // Still running: the socket's status and the bet's odds -- for cockfight,
    // only the final odds, once betting has closed.
    pendingFields(ticket, liveStatus, taiXiu) {
      return {
        status: this.statusLabel(liveStatus),
        statusKey: this.statusKey(liveStatus),
        ...(taiXiu ? {} : this.cockfightLiveOutcome(ticket, liveStatus)),
        payout: taiXiu ? this.runningOddsText(ticket, taiXiu) : this.cockfightOddsText(ticket, liveStatus),
        payoutKind: 'odds',
      };
    },

    /*
     * A cockfight round that has its result while the ticket is not settled
     * yet: the winning side from the live fight ("Banker Wins", in banker's
     * colour), as the settled row shows it -- not a bare RESULT (PO feedback).
     */
    cockfightLiveOutcome(ticket, liveStatus) {
      if (!this.hasResultStatus(liveStatus)) return {};
      const fightId = ticket.fight && ticket.fight.id;
      const liveFight = fightId && this.cockfightFightsById[fightId];
      if (!liveFight || !liveFight.result) return {};
      const withResult = { ...ticket, fight: { ...ticket.fight, result: liveFight.result } };
      const key = this.outcomeKey(withResult);
      if (key === 'settled') return {};
      return { status: this.outcomeLabel(withResult), statusKey: key };
    },

    /*
     * Cockfight odds are pool odds: they change with every bet until betting
     * closes, so the odds at placing are not what the bet pays. Nothing is
     * shown while betting is open; after that, the ticket's own odds if the
     * backend has set them, else the round's odds as they closed.
     */
    cockfightOddsText(ticket, liveStatus) {
      // No live status means the round has already been replaced: it closed.
      if (COCKFIGHT_BET_OPEN_STATUSES.includes(liveStatus)) return NO_ODDS;
      const own = Number(ticket.odds);
      const fightId = ticket.fight && ticket.fight.id;
      const odds = Number.isFinite(own) && own > 0 ? own : this.finalOdds[`${fightId}:${ticket.selection}`];
      return odds ? `${Number(odds).toFixed(2)}x` : NO_ODDS;
    },

    // Keep each closed round's odds (draw +1, as the bet zone shows it); the
    // last value seen while the round is still on screen is the final one.
    captureFinalOdds() {
      Object.keys(this.cockfightFightsById).forEach((fightId) => {
        const fight = this.cockfightFightsById[fightId];
        if (!fight.status || COCKFIGHT_BET_OPEN_STATUSES.includes(fight.status)) return;
        (fight.selections || []).forEach((selection) => {
          const key = `${fightId}:${selection.name}`;
          const odds = Number(selection.odds);
          if (!(odds > 0)) return;
          const shown = selection.name === 'draw' ? odds + 1 : odds;
          if (this.finalOdds[key] !== shown) this.$set(this.finalOdds, key, shown);
        });
      });
    },

    /*
     * A running cockfight ticket comes back with `odds: 0`, so the odds shown
     * are the ones captured when it was placed (`bets/submitBet`). One
     * selection can be bet more than once a round, so the capture nearest the
     * ticket's creation time is the one that belongs to it.
     */
    /*
     * The odds a running ticket is shown at: its own once the backend sets
     * them, else the ones captured at placing. Compared as numbers -- a running
     * ticket's `odds` can arrive as the string "0.00", which is truthy, so
     * `ticket.odds || captured` kept the zero and never reached the capture.
     */
    // Tài Xỉu odds are Euro on the ticket and the table; shown as game odds,
    // as the bet history shows them (1.97 -> 1:0.97, 121 -> 1:120, PO feedback).
    // Cockfight keeps `1.62x`.
    runningOddsText(ticket, taiXiu) {
      const odds = (taiXiu && this.tableOdds(ticket)) || this.runningOdds(ticket);
      const gameOdds = taiXiu ? taiXiuGameOdds(odds) : null;
      if (gameOdds !== null) return formatTaiXiuOdds(gameOdds).replace(/ /g, '');
      return taiXiu && odds > 0 ? String(parseFloat(odds.toFixed(4))) : `${odds.toFixed(2)}x`;
    },
    /*
     * A Tài Xỉu bet's odds from its table's bet-type-odds (`table.odds`, Euro,
     * by bet type) -- what the board sells it at. 0 when the table or the bet
     * type has none yet, so the caller falls back to the ticket's own.
     */
    tableOdds(ticket) {
      const arena = (ticket.fight && ticket.fight.matchDay && ticket.fight.matchDay.arena) || {};
      if (!arena.id) return 0;
      const table = (this.tables || []).find((t) => t.id === arena.id || t.arenaId === arena.id);
      const betType = this.betType(ticket);
      const value = table && table.odds && betType !== undefined ? Number(table.odds[betType]) : 0;
      return Number.isFinite(value) && value > 0 ? value : 0;
    },
    runningOdds(ticket) {
      const own = Number(ticket.odds);
      if (Number.isFinite(own) && own > 0) return own;
      return Number(this.capturedOdds(ticket)) || 0;
    },
    capturedOdds(ticket) {
      const fightId = ticket.fight && ticket.fight.id;
      const captures = fightId ? this.placedOdds[placedOddsKey(fightId, ticket.selection)] : null;
      if (!captures || !captures.length) return 0;
      const createdAt = Date.parse(ticket.createdDateTime);
      if (Number.isNaN(createdAt)) return captures[captures.length - 1].odds;
      return captures.reduce((best, entry) =>
        Math.abs(entry.at - createdAt) < Math.abs(best.at - createdAt) ? entry : best
      ).odds;
    },

    // Settled: the round's result and what the bet paid.
    settledFields(ticket, taiXiu) {
      const amount = Number(ticket.winLossAmount || 0);
      const dice = this.resultDice(ticket);

      return {
        status: this.outcomeLabel(ticket),
        // The bar reports the round, not the member's win or loss -- the
        // payout beside it already says that. So it is keyed by the side that
        // won, which is what gives it its colour.
        statusKey: this.outcomeKey(ticket),
        payout: `${amount > 0 ? '' : ''}${formatAmount(amount)}`,
        payoutKind: amount >= 0 ? 'win' : 'loss',
        // A settled Tài Xỉu bet shows its dice in place of a status pill.
        ...(taiXiu && dice ? { result: dice } : {}),
      };
    },

    /*
     * The ticket identifies its venue as `fight.matchDay.arena`. The arena id is
     * the join key to the lists the client already holds, so it is tried first
     * and the raw code is only a fallback.
     *
     * `matchDay.code` is the match day ("M010243"), not the table -- using it
     * is what put a match-day id in the table column.
     */
    tableCode(ticket, taiXiu) {
      const arena = (ticket.fight && ticket.fight.matchDay && ticket.fight.matchDay.arena) || {};

      const table = (this.tables || []).find((t) => t.id === arena.id);
      if (table) return table.code;

      const cockfightArena = (this.arenas || []).find((a) => a.id === arena.id);
      if (cockfightArena) return cockfightArena.code;

      if (arena.code) return String(arena.code).toUpperCase();
      return taiXiu ? '' : this.selectedArena.code || '';
    },

    betType(ticket) {
      return normalizeTaiXiuTicketBetType(ticket.betType, ticket.selection);
    },

    isTotalBet(ticket) {
      const betType = this.betType(ticket);
      return betType >= TaiXiuBetType.total4 && betType <= TaiXiuBetType.total17;
    },

    /*
     * Drives the pill's colour, following the board: Tài is the red side, Xỉu
     * the blue one, both parity markets the green block, and the totals and the
     * face markets the blue cells.
     */
    sideKey(ticket, taiXiu) {
      const selection = String(ticket.selection || '').toLowerCase();
      if (!taiXiu) return selection;
      if (TX_FACE_BET_TYPES.includes(this.betType(ticket))) return 'dice';
      if (this.isTotalBet(ticket)) return 'sum';
      if (['xiu', 'xỉu', 'small'].includes(selection)) return 'xiu';
      if (['tai', 'tài', 'big'].includes(selection)) return 'tai';
      if (['le', 'lẻ', 'odd'].includes(selection)) return 'le';
      if (['chan', 'chẵn', 'even'].includes(selection)) return 'chan';
      return selection;
    },

    sideLabel(ticket, taiXiu) {
      const selection = String(ticket.selection || '').toLowerCase();
      if (!taiXiu) return SIDE_LABELS[selection] || selection.toUpperCase();
      // A face market is an icon badge instead (see `market`).
      if (TX_FACE_BET_TYPES.includes(this.betType(ticket))) return '';
      if (this.isTotalBet(ticket)) return selection.toUpperCase();
      if (['xiu', 'xỉu', 'small'].includes(selection)) return this.$t('Small');
      if (['tai', 'tài', 'big'].includes(selection)) return this.$t('Big');
      if (['le', 'lẻ', 'odd'].includes(selection)) return this.$t('Odd');
      if (['chan', 'chẵn', 'even'].includes(selection)) return this.$t('Even');
      return selection.toUpperCase();
    },

    /*
     * The live round's status, or -- once the round has a result, or has been
     * replaced by the next one before the ticket settled -- RESULT.
     */
    liveStatus(liveStatus) {
      return LIVE_STATUSES[liveStatus] || RESULT_STATUS;
    },

    statusLabel(liveStatus) {
      return this.liveStatus(liveStatus).label;
    },

    statusKey(liveStatus) {
      return this.liveStatus(liveStatus).key;
    },

    /*
     * `ticket.result` is the member's own outcome (win/loss/void); the round's
     * result is on the fight. Reading the former is what put "LOSS" in the bar
     * where the design wants "PLAYER WIN".
     */
    fightResult(ticket) {
      return String((ticket.fight && ticket.fight.result) || '').toLowerCase();
    },

    outcomeLabel(ticket) {
      const voided = String(ticket.result || '').toLowerCase();
      if (voided === 'cancelled' || voided === 'void') return voided.toUpperCase();

      const result = this.fightResult(ticket);
      if (result === 'banker') return 'Banker Wins';
      if (result === 'player') return 'Player Wins';
      if (result === 'draw') return 'Draw';
      return result ? result.toUpperCase() : 'Settled';
    },

    outcomeKey(ticket) {
      const voided = String(ticket.result || '').toLowerCase();
      if (voided === 'cancelled' || voided === 'void') return 'void';

      const result = this.fightResult(ticket);
      if (['banker', 'player', 'draw'].includes(result)) return result;
      return 'settled';
    },

    /*
     * `fight.result` for Tài Xỉu is the three dice; the board parses the same
     * field, so the shape is whatever the backend sends there.
     */
    resultDice(ticket) {
      const raw = (ticket.fight && ticket.fight.result) || '';
      const faces = String(raw)
        .split(/[^1-6]+/)
        .filter(Boolean)
        .map(Number)
        .slice(0, 3);
      if (faces.length !== 3) return null;
      const total = faces.reduce((sum, face) => sum + face, 0);
      return {
        dice: faces,
        total,
        label: `${total > 10 ? this.$t('Big') : this.$t('Small')} / ${total % 2 ? this.$t('Odd') : this.$t('Even')}`,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
/*
 * v2.1 prototype: one 288px card -- tabs, the scrolling list, and the quick
 * play bar as its footer. Its height is the body row's (desktop-client).
 */
$rail-width: 288px;

.side-rail {
  flex: 0 0 $rail-width;
  width: $rail-width;
  min-width: 0;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}

.side-rail__card {
  border-radius: 12px;
  border: 1px solid $ga-border-subtle;
  background-color: $ga-rail-bg;
  box-sizing: border-box;
  overflow: hidden;
}

.side-rail__card--list {
  // Anchors the quick play sheet, which opens over the list.
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

/*
 * The history component is the mobile one, authored for a full-width phone. At
 * 296px two things broke, and both are fixed from here so mobile is untouched:
 *
 *  - the toolbar needs 319px (date select 96 + All 54 + two 32px icons + gaps,
 *    plus a 73px date label). The label is dropped: the date select right next
 *    to it already says which range is showing, so it was the redundant one.
 *  - the table's fixed columns total 280px inside a 272px box, which left the
 *    flexible Round column negative and pushed the row to 308px -- the sideways
 *    scroll, with Total falling off the edge.
 */
/*
 * The date dropdown is absolutely positioned, and `.bet-history-list` clips to
 * its own content height -- so as soon as the list was short, the open dropdown
 * was cut off part-way down with no way to reach the options below. The panel
 * lets it out (the scrolling `.side-rail__body` above it is the real clipper,
 * and it has room), and the list caps its own height so a short rail scrolls
 * the options rather than hiding them.
 */
.side-rail__history.side-rail__history {
  overflow: visible;
}

.side-rail__history ::v-deep {
  .bet-history-list__date-select .items {
    max-height: 176px;
    overflow-y: auto;
  }

  .bet-history-list__date-label {
    display: none;
  }

  .bet-history-list__filters {
    flex-wrap: nowrap;
  }

  /*
   * The rail's table is 286 wide, 270 inside an 8px inset. The table's own
   * columns (round 64 | bet >= 60 | market 72 | odds 40 | total 64, 4 apart)
   * come to ~316 and ran the header and rows off the edge, so here they are:
   *   round 54 | bet 1fr (min 0) | market 62 | odds 34 | total 58, 3 apart
   *   = 220 + 12 = 232, the bet column taking the remaining ~38.
   * Flex bases, not widths: the table sizes its columns with `flex`.
   */
  .table-header,
  .table-row,
  .sub-row {
    padding-left: 8px;
    padding-right: 8px;
  }

  // No fixed columns to fit, so the divider keeps a comfortable inset.
  .day-divider {
    padding-left: 10px;
    padding-right: 10px;
  }

  .table .table-header-cell.round,
  .table .table-cell.round {
    flex: 0 0 54px;
    margin-right: 3px;
  }

  .table .table-header-cell.bet,
  .table .table-cell.bet {
    flex: 1 1 0;
    min-width: 0;
    margin-right: 3px;
  }

  .table .table-header-cell.market,
  .table .table-cell.market {
    flex: 0 0 62px;
    width: 62px;
    margin-right: 0;
  }

  .table .table-header-cell.odds,
  .table .table-cell.odds {
    flex: 0 0 34px;
    width: 34px;
    margin-left: 3px;
    white-space: nowrap;
  }

  // Wide enough for a status pill ("CANCELLED") and "+485.00".
  .table .table-header-cell.total,
  .table .table-cell.total {
    flex: 0 0 58px;
    width: 58px;
    margin-left: 3px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .table .table-header-cell {
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.04em;
    color: $ga-text-muted;
  }

  .table .table-row {
    min-height: 40px;
    height: 40px;
    border-bottom-color: $ga-border-subtle;
  }

  .table .table-cell {
    font-size: 11px;
  }

  .table .round__time {
    font-size: 10px;
  }

  // Under the cell's classes to outrank the table's own `.table .total .result-status`.
  .table-cell.total .result-status {
    max-width: 100%;
    padding: 2px 3px;
    font-size: 9px;
    letter-spacing: -0.2px;
    white-space: nowrap;
    overflow: hidden;
    box-sizing: border-box;
  }

  .market-badge {
    min-width: 0;
    max-width: 62px;
    padding: 2px 4px;
    font-size: 10px;
  }

  /*
   * Multi-selection ticket: its odds are only "—", so that column gives 16 to
   * the "N BETS" pill.
   */
  .table .table-row.is-multi {
    .table-cell.market {
      flex: 0 0 78px;
      width: 78px;
    }

    .table-cell.odds {
      flex: 0 0 18px;
      width: 18px;
      text-align: center;
    }
  }

  .table .bets-toggle {
    height: 20px;
    padding: 0 5px;
    gap: 2px;
    border-width: 1px;
    font-size: 9px;
    letter-spacing: 0;
    white-space: nowrap;
  }

  .table .multi-status {
    gap: 2px;
    font-size: 9px;
    letter-spacing: 0;
  }

  // Selections sit just inside the row's own 8px inset, as the prototype.
  .table .sub-row {
    padding-left: 16px;
    padding-right: 8px;
  }

  .table .sub-row .table-cell.total.pending {
    font-size: 10px;
  }

  // Nothing should scroll sideways once the row fits.
  .result-main-body {
    overflow-x: hidden;
  }
}

/* `‹ BET DETAIL` bar. The shared popup draws its own; in the rail this is it. */
.side-rail__back {
  width: 100%;
  padding: 12px 14px;
  border: 0;
  border-bottom: 1px solid #19253c;
  background: transparent;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.side-rail__back-chevron {
  color: $ga-gold-300;
  font-size: 20px;
  line-height: 1;
}

/*
 * The detail is the mobile component, authored for a full-width phone. At 296px
 * its label/value table has to give: the rows go tighter and the copy-id pill
 * wraps under the ticket number instead of beside it.
 */
.side-rail__detail ::v-deep {
  /*
   * Three paddings stack on the way in -- this wrapper, then `.table`, then
   * each cell -- which came to 44px a side and squeezed the label column down
   * to 95px. Trimmed to 8 + 4 + 10, giving roughly 20px a side back to the
   * text.
   */
  // padding: 12px 8px;

  .ticket-header {
    flex-wrap: wrap;
    gap: 6px;
  }

  .detail-card .table-row {
    min-height: 0;
  }

  /*
   * The label column is narrow here, so the longest label ("COMMISSION") was
   * painting past its own panel. It wraps rather than widening the column, but
   * only between words -- breaking mid-word gave "COMMISSIO / N" -- and drops a
   * point so the long ones still land on one line.
   */
  .detail-card .table-header {
    white-space: normal;
    word-break: normal;
    overflow-wrap: normal;
    font-size: 12px;
    line-height: 1.2;
  }

  .detail-card .table-cell {
    font-size: 12px;
  }

  .detail-card .table-header,
  .detail-card .table-cell {
    padding-left: 10px;
    padding-right: 10px;
  }
}

.side-rail__quickplay {
  flex: 0 0 auto;
}

.side-rail__tabs {
  flex: 0 0 44px;
  height: 44px;
  display: flex;
}

.side-rail__tab {
  flex: 1 1 50%;
  border: 0;
  background-color: #051620;
  color: #6a91a7;
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  box-shadow: inset 0 -2px 0 transparent;
  cursor: pointer;

  &.active {
    color: #fff;
    background-color: #072231;
    box-shadow: inset 0 -2px 0 #d5a354;
    cursor: default;
  }
}

// Scrolls within the rail's fixed height, with a slim visible scrollbar.
.side-rail__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: $ga-navy-600 transparent;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 2px;
    background: $ga-navy-600;
  }
}

/*
 * A sixth bet pushes the oldest out of the five the design allows. The leaving
 * row is taken out of flow so the ones above slide up while it fades, instead
 * of the list jumping.
 */
.rail-row-leave-active {
  position: absolute;
  left: 0;
  right: 0;
  transition: opacity 0.28s ease, transform 0.28s ease;
}

.rail-row-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

.rail-row-enter-active {
  transition: opacity 0.28s ease, transform 0.28s ease;
}

.rail-row-enter {
  opacity: 0;
  transform: translateY(-8px);
}

.rail-row-move {
  transition: transform 0.28s ease;
}

.side-rail__rows {
  // Anchors the absolutely positioned row on its way out.
  position: relative;
  margin: 0;
  padding: 0;
  list-style: none;
}

</style>
